import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { FeedbackButton } from "@/components/app/feedback-button";
import { NoWorkspaces } from "@/components/app/no-workspaces";
import { ReconnectPill } from "@/components/app/reconnect-pill";
import { ScreenShareIndicator } from "@/components/app/screen-share-indicator";
import { WorkspaceRevoked } from "@/components/app/workspace-revoked";
import { ConnectionWaitScreen } from "@/components/auth/connection-wait";
import { useConnectionWait } from "@/hooks/use-connection-wait";
import { useSendToSignIn } from "@/hooks/use-send-to-sign-in";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { initActivityTracker } from "@/lib/activity-tracker";
import { awayStore, returnHome } from "@/lib/lti/instance-switch";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

export const Route = createFileRoute("/app")({
	component: RouteComponent,
});

function RouteComponent() {
	const user = useStore(authStore, (state) => state.user);
	const initialized = useStore(authStore, (state) => state.initialized);
	const { isConnected, reconnectCountdown, connectionEpoch } = useWsConnected();
	const wait = useConnectionWait(isConnected);

	const logout = useStore(authStore, (state) => state.logout);
	const loadOnConnect = useStore(coreStore, (state) => state.loadOnConnect);
	// D2: hydrated cached channels let us paint /app before the WS connects. Only
	// a cold cache miss falls back to the full-screen spinner.
	const hasShell = useStore(coreStore, (state) => state.channels.size > 0);
	// TECH-2958: terminal full-screen states for cold (uncached) loads.
	const connectStatus = useStore(coreStore, (state) => state.connectStatus);
	const workspacesElsewhere = useStore(
		coreStore,
		(state) => state.workspacesElsewhere
	);
	const workspacesClosed = useStore(
		coreStore,
		(state) => state.workspacesClosed
	);
	// Which way access ended, so the terminal screen below says the true thing
	// (TECH-3253).
	const revokedReason = useStore(coreStore, (state) => state.revokedReason);
	// Decides the revoked screen's action (TECH-3259). `revokeWorkspaceAccess`
	// has already removed the revoked workspace from this map, so anything left
	// is somewhere the student can actually go; with nothing left, offering the
	// picker would just reload into the `no-workspaces` screen below.
	const hasOtherWorkspaces = useStore(
		coreStore,
		(state) => state.workspaces.size > 0
	);
	const away = useStore(awayStore, (state) => state.away);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const currentWorkspaceName = useStore(coreStore, (state) =>
		currentWorkspaceId
			? (state.workspaces.get(currentWorkspaceId)?.name ?? null)
			: null
	);

	// Take the tab's single socket for the Learner Client's data. A no-op unless we
	// are arriving from another client, which loaded its own.
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	useSendToSignIn();

	useEffect(() => {
		if (!SENTRY_ENABLED) return;
		if (user) {
			Sentry.setUser({ id: user.uid, email: user.email || undefined });
		} else if (initialized) {
			Sentry.setUser(null);
		}
	}, [initialized, user]);

	useEffect(() => {
		if (!isConnected) return;

		// Sequential one-in-flight retrieval chain (workspace.setCurrent →
		// channel.list → message.list → users.list → roles.list). The store's
		// generation token abandons any chain superseded by a newer run, so rapid
		// reconnects collapse to just the latest chain. TECH-3051: `connectionEpoch`
		// re-fires this on EVERY reconnect. The grace period keeps `isConnected`
		// true across a fast close→open, so without the epoch the reconnect would
		// skip the reload and leave the server connection workspace-less
		// (stale users → author names render as raw Firebase UIDs).
		loadOnConnect();
	}, [isConnected, connectionEpoch, loadOnConnect]);

	// Activity tracker is mount-scoped, so initialise once, not on every reconnect.
	useEffect(() => initActivityTracker(), []);

	if (!(initialized && user)) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading...</p>
			</div>
		);
	}

	// Terminal: access to the workspace on screen was revoked mid-session
	// (TECH-3259). Checked ahead of the remaining states, including the
	// `hasShell` fallback, which would otherwise keep rendering the cached
	// workspace we have just been removed from. Where the student goes next
	// depends on what they have left. A reload lands them in a remaining
	// workspace (the revoked one is gone from the server's list and its cached
	// channels have been dropped), and with none left the only honest action is
	// to sign out rather than reload into the `no-workspaces` screen below.
	if (connectStatus === "revoked") {
		return (
			<WorkspaceRevoked
				hasOtherWorkspaces={hasOtherWorkspaces}
				onContinue={() => window.location.reload()}
				onLogout={logout}
				onReturnHome={
					away
						? async () => {
								const outcome = await returnHome(
									away.homeWorkspaces[0]?.id ?? null
								);
								if (!outcome.ok) toast.error(outcome.message);
							}
						: undefined
				}
				reason={revokedReason ?? "removed"}
				workspaceName={away?.workspaceName ?? null}
			/>
		);
	}

	// Terminal: the user has no workspaces (list loaded, empty). Persistent state,
	// not a transient load, so show a clear message rather than a blank page.
	if (connectStatus === "no-workspaces") {
		return (
			<NoWorkspaces
				closed={workspacesClosed}
				onLogout={logout}
				workspacesElsewhere={workspacesElsewhere}
			/>
		);
	}

	// Terminal: critical data failed all retries AND there is nothing cached to
	// fall back to. (With a cache, we keep rendering it read-only plus a toast.)
	if (connectStatus === "error" && !hasShell) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
				<p className="font-medium">Couldn't load your workspace</p>
				<p className="text-muted-foreground text-sm">
					Something went wrong loading your channels. Please refresh to try
					again.
				</p>
				<button
					className="rounded-md bg-primary px-4 py-2 text-primary-foreground text-sm"
					onClick={() => window.location.reload()}
					type="button"
				>
					Refresh
				</button>
			</div>
		);
	}

	// Cold cache miss (first-ever load, or a workspace with nothing cached), so
	// hold the full screen until the socket opens and the chain populates
	// channels. With a cached shell, the pill below says it is reconnecting.
	if (!(isConnected || hasShell)) {
		return <ConnectionWaitScreen wait={wait} />;
	}

	return (
		<>
			{!isConnected && (
				<ReconnectPill
					countdown={reconnectCountdown}
					workspaceName={currentWorkspaceName}
				/>
			)}
			<Outlet />
			<FeedbackButton />
			<ScreenShareIndicator />
		</>
	);
}
